import {Component, EventEmitter, Input, Output} from '@angular/core';
import {Line} from '@app/shared/models/line.model';
import {TableConfig} from '@app/shared/util/table-config.utils';
import {Group} from '@app/tool/model/group.model';
import {Tool} from '@app/tool/model/tool.model';
import {ToolSet} from '@app/tool/model/toolSet.model';

@Component({
    selector: 'app-part-number-tools-table',
    templateUrl: './part-number-tools-table.component.html',
    styleUrls: ['./part-number-tools-table.component.scss']
})
export class PartNumberToolsTableComponent {

    @Input() toolsGrouped = new Array<Group>();
    @Input() selectedTools = new Array<Tool | ToolSet>();
    @Input() selectedLine: Line;
    @Input() tableConfig: TableConfig;
    @Input() isLoading = false;
    @Input() showFilters = true;
    @Input() showGroupFilters = false;
    @Input() filtersToggle = true;
    @Input() showGroupers = false;
    @Input() isFilterExpanded = false;
    @Input() definedGroupers = false;
    @Input() showPagination = true;
    @Input() readonly = false;

    @Output() pageChange = new EventEmitter<any>();
    @Output() filterChange = new EventEmitter<any>();
    @Output() filterGroupChange = new EventEmitter<any>();
    @Output() groupChange = new EventEmitter<any>();
    @Output() sortChange = new EventEmitter<any>();
    @Output() onToolClicked = new EventEmitter<{ checked: boolean, group: Tool | ToolSet }>();

    groupableColumns = [];

    constructor() {
        // We need at least one groupable column to display the tools under a tree
        this.groupableColumns = [
            {
                id: 0,
                value: "ToolDetailsId"
            },
        ];
    }

    onPageChange(tableConfig: TableConfig) {
        this.pageChange.emit(tableConfig);
    }

    onSortChange(tableConfig: TableConfig) {
        this.sortChange.emit(tableConfig);
    }

    onFilterChange(tableConfig: TableConfig) {
        this.filterChange.emit(tableConfig);
    }

    onFilterGroupChange(tableConfig: TableConfig) {
        this.filterGroupChange.emit(tableConfig);
    }

    onGroupChange(tableConfig: TableConfig) {
        this.groupChange.emit(tableConfig);
    }

    isToolSet(record) {
        return record instanceof ToolSet;
    }

    getSetForTool(tool: Tool): ToolSet | null {
        for (const el of this.toolsGrouped) {
            if (el instanceof ToolSet) {
                for (const toolOfSet of el.tools) {
                    if (toolOfSet.id === tool.id) {
                        return el;
                    }
                }
            }
        }
        return null;
    }

    isToolSelected(tool: Tool | ToolSet) {
        return this.selectedTools.filter(selectedTool => (
                (selectedTool instanceof Tool && tool instanceof Tool && selectedTool.id === tool.id) ||
                (selectedTool instanceof ToolSet && tool instanceof ToolSet && selectedTool.name === tool.name)
            )
        ).length > 0;
    };

    onClickTool(event: any, record: Tool | ToolSet) {
        event.stopPropagation();
        if (record instanceof ToolSet) {
            this.handleSingleToolSetSelected(record);
            this.onToolClicked.emit({checked: event.target.checked, group: record});
        } else if (record instanceof Tool) {
            const setForSelectedTool = this.getSetForTool(record);
            if (event.target.checked) {
                this.handleToolChecked(record, setForSelectedTool)
            } else {
                this.handleToolUnchecked(record, setForSelectedTool);
            }
        }
    }

    isSelectionDisabled(record) {
        return record.disabled || this.readonly;
    }

    hasCheckbox(record: any) {
        return (record instanceof Tool || record instanceof ToolSet);
    }

    toolSetContainsTool(tool: Tool, toolSet: ToolSet): boolean {
        return toolSet.tools.filter(toolFromToolSet => toolFromToolSet.id === tool.id).length > 0;
    }

    isChecked(record: Tool | ToolSet) {
        if (this.isToolSelected(record)) {
            return true;
        } else if (record instanceof Tool) {
            return this.selectedTools.filter(
                tool => tool instanceof ToolSet &&
                    this.toolSetContainsTool(record, tool)
            ).length > 0;
        }
    }

    private handleToolUnchecked(unselectedTool, setForUnselectedTool) {
        if (setForUnselectedTool && this.isToolSelected(setForUnselectedTool)) {
            this.onToolClicked.emit({checked: false, group: setForUnselectedTool});
            for (const selectedTool of setForUnselectedTool.tools) {
                if (selectedTool.id !== unselectedTool.id) {
                    this.onToolClicked.emit({checked: true, group: selectedTool});
                }
            }
        } else {
            this.onToolClicked.emit({checked: false, group: unselectedTool});
        }
    }


    private handleSingleToolSetSelected(toolSet: ToolSet) {
        const selectedSubtools = toolSet.tools.filter(tool => this.isToolSelected(tool));
        for (const selectedSubTool of selectedSubtools) {
            this.onToolClicked.emit({checked: false, group: selectedSubTool})
        }
    }

    private handleToolChecked(selectedTool: Tool, setForSelectedTool: ToolSet) {
        if (setForSelectedTool) {
            let allChecked = true;
            for (const tool of setForSelectedTool.tools) {
                if (tool.id !== selectedTool.id && !this.isToolSelected(tool)) {
                    allChecked = false;
                }
            }
            if (allChecked) {
                for (const tool of setForSelectedTool.tools) {
                    if (this.isToolSelected(tool)) {
                        this.onToolClicked.emit({checked: false, group: tool});
                    }
                }
                this.onToolClicked.emit({checked: true, group: setForSelectedTool});
            } else {
                this.onToolClicked.emit({checked: true, group: selectedTool});
            }
        } else {
            this.onToolClicked.emit({checked: true, group: selectedTool});
        }
    }
}
